'use client'

import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { useProductPricingPolicy } from '@/hooks/react-query/products/use-product'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { CAMPAIGN_NAMES } from '@/lib/utils/campaign/campaign-names'
import { isQrEnhancedMode } from '@/lib/utils/product/is-qr-enhanced-mode'
import { useCampaign } from '@/providers/campaign-provider'
import { ERankingLevel } from '@/types/auth'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import React, { useMemo } from 'react'
import { PRODUCT_SIZE } from '../../../../../../lib/constants/size'
import { useProductDetailSafe } from '../../context/product-detail-context'
import { useProductDetailStore } from '../../hooks/use-product-detail-store'
import { ProductInfo } from './module'

const RANKING_ID_TO_LABEL: Record<string, string> = {
  [ERankingLevel.NEW]: 'new_user',
  [ERankingLevel.SILVER]: 'silver',
  [ERankingLevel.GOLD]: 'gold',
  [ERankingLevel.PLATINUM]: 'platinum',
}

// Thứ tự rank từ thấp đến cao
const RANKING_ORDER = [
  ERankingLevel.NEW,
  ERankingLevel.SILVER,
  ERankingLevel.GOLD,
  ERankingLevel.PLATINUM,
]

const InfoBarMobile = dynamic(
  () => import('../info-bar/info-bar-mobile').then((m) => m.InfoBarMobile),
  { ssr: false }
)

type Props = {
  className?: string
}

export const ProductInfoView: React.FC<Props> = React.memo(
  function ProductInfoView({ className }) {
    const { product } = useProductDetailSafe()
    const isMobile = useIsMobileClient()
    const { data: productPricingPolicy } = useProductPricingPolicy(product.id)
    const sizeType = PRODUCT_SIZE[product.sizechart]
    const t = useTranslations('product_detail.title')

    const { productData, isFlashSale, isCoolClubSale, productFlashSale } =
      useProductDetailStore()

    // Map rankings id sang label và sắp xếp theo thứ tự từ thấp đến cao
    const rankingsText = useMemo(() => {
      const rankings = productData.coolclub_sale?.rankings
      if (!rankings || rankings.length === 0) return 'CoolClub'

      // Sắp xếp rankings theo thứ tự từ thấp đến cao
      const sortedRankings = RANKING_ORDER.filter((rankId) =>
        rankings.includes(rankId)
      )

      const rankingLabels = sortedRankings
        .map((id) => {
          const label = RANKING_ID_TO_LABEL[id]
          return label ? t(`ranking_names.${label}`) : null
        })
        .filter((label): label is string => label !== null)

      return rankingLabels.length > 0 ? rankingLabels.join(', ') : 'CoolClub'
    }, [productData.coolclub_sale?.rankings, t])

    // Memo hóa các giá trị truyền xuống component con
    const addCartProps = useMemo(
      () => ({
        isStopSale: product.product_stoppage,
        isGiveaway: product.giveaway_all,
        isDisabled: !Object.entries(productData.mapped_variants).length,
      }),
      [product, productData]
    )

    //param preview
    const searchParams = useSearchParams()
    const preview = searchParams.get('preview')
    // Layout QR thay thế (ProductGalleryQr/InfoBarMobileQr) chỉ mount trên
    // mobile (product-detail-template.tsx isQrMobile) — phải cùng điều kiện
    // isMobile ở đây, nếu không desktop/tablet mở link QR sẽ mất luôn giá,
    // chọn màu/size và nút mua hàng mà không có gì thay thế.
    const isQrEnhanced = isQrEnhancedMode(searchParams) && isMobile

    // check xem category_ids có chứa 67b2b03ce818f82910825227
    const isFemaleProduct = productData?.category_ids?.includes(
      '67b2b03ce818f82910825227'
    )

    const hasIconThumbnail =
      !product?.icon_thumbnail?.hide && !!product?.icon_thumbnail?.image

    const campaignData = useCampaign(CAMPAIGN_NAMES.CAMP_20_10)

    return (
      <section
        className={cn(
          'flex flex-col gap-2 bg-light px-5 pt-5.5 lg:p-0',
          className
        )}
        aria-label='Product Information'
      >
        <div>
          {/* QR mobile: ẩn rating/share, không cần thiết trong layout này */}
          {!isQrEnhanced && (
            <div className='mb-3 flex items-center justify-between gap-2 border-b border-neutral-200 pb-3 lg:mb-4'>
              <ProductInfo.RatingSummary
                rating={product.review}
                soldProduct={product.sold_product}
              />
              {/* <div className='shrink-0 whitespace-nowrap'>
                <ProductInfo.Sharing />
              </div> */}
            </div>
          )}

          <h1 className='line-clamp-3 font-criteria text-2xl font-semibold leading-tight text-neutral-900 md:text-3xl lg:text-4xl'>
            {product.display_name}
          </h1>
          {product.display_name_open && (
            <p className='mt-1 text-sm leading-5 text-neutral-600 lg:text-base'>
              {product.display_name_open}
            </p>
          )}
          {product.spu && (
            <p className='mt-1 text-sm leading-5 text-neutral-600 lg:text-base'>
              {t('product_code')}: {product.spu}
            </p>
          )}

          {isFlashSale &&
          productFlashSale?.endsAt &&
          productFlashSale?.product ? (
            <ProductInfo.FlashSale
              endsAt={productFlashSale.endsAt}
              product={productFlashSale.product}
            />
          ) : (
            <ProductInfo.Price
              comparePrice={productData.compare_price}
              regularPrice={productData.regular_price}
              salePercent={productData.sale_percent}
              isCoolClubSale={isCoolClubSale}
              tags={productData.tags}
              product={product}
              rankingsText={rankingsText}
            />
          )}

          {!isCoolClubSale && productData.coolclub_sale?.active && (
            <ProductInfo.CoolclubPricing
              coolclubPrice={productData.coolclub_sale?.coolclub_price}
              rankingsText={rankingsText}
            />
          )}
        </div>

        <div
          className='mt-4 space-y-4 md:mt-7'
          id='product-detail-variants'
          role='region'
          aria-label='Product Options'
        >
          <div className='flex flex-col gap-2.5'>
            {hasIconThumbnail && (
              <div className='hidden w-full overflow-hidden rounded-[2px] lg:block'>
                <Image
                  src={ImageHelper.getImageUrl(
                    product.icon_thumbnail.image,
                    '',
                    750
                  )}
                  alt={product.display_name || product.title}
                  width={547}
                  height={60}
                  className='h-auto w-full object-contain'
                  priority
                />
              </div>
            )}
            <ProductInfo.Vouchers productId={product.id} />
            <ProductInfo.CoolcashReward
              regularPrice={productData.regular_price}
              leading={<ProductInfo.Freeship price={product.regular_price} />}
            />
          </div>
          {product?.addon_note && (
            <p className='text-sm font-bold italic text-neutral-900'>
              {product?.addon_note}
            </p>
          )}
          {productPricingPolicy && (
            <ProductInfo.PricingPolicy description={productPricingPolicy} />
          )}
          {product?.is_setdo ? (
            <ProductInfo.SizesCombo />
          ) : (
            <>
              <ProductInfo.Variants />
              <ProductInfo.Sizes
                sizeChart={sizeType}
                gender_type={product.gender_type}
                size_guide={product.size_guide}
              />
            </>
          )}
          {!preview ? <ProductInfo.AddCart {...addCartProps} /> : null}
          {product?.exclude_discount && (
            <div className='text-xs italic text-primary' role='alert'>
              * {t('discount_not_apply')}
            </div>
          )}
          <ProductInfo.PromotionGift productId={product.id} />

          {isFemaleProduct && campaignData?.isActive ? (
            <Image
              src={ImageHelper.getImageUrl(
                '/image/October2025/mceclip0_49.jpg',
                '',
                160
              )}
              alt='female-product'
              width={80}
              height={80}
              className='w-full'
            />
          ) : null}
          <ProductInfo.Promotion />
          <ProductInfo.Policy
            gender={product.gender_type}
            collections={product.collections || []}
          />
          {/* QR mobile: card đã render riêng ở ProductDetailTemplate, ngay sau hero full-screen */}
          {isQrEnhanced ? null : isMobile && !preview ? (
            <InfoBarMobile
              isStopSale={product.product_stoppage}
              isGiveaway={product.giveaway_all}
            />
          ) : null}
        </div>
      </section>
    )
  }
)
